/**
 * All Database interactions for the products table
 */

import * as Sentry from "@sentry/browser";
import { supabase } from "../../lib/supabase";
import { Order, CartItem, Product } from "../../commonTypes";

export type ProductFilters = {
  tagSlugs?: string[]; // Expanded filter slugs like ["category-dresses", "size-6-9m", "color-blue"]
  searchText?: string;
  onlyAvailable?: boolean;
  previousProductVariantIds?: number[]; // For "previously rented" filter
};

export type PaginatedProductsResult = {
  products: Product[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
};

export const getProductById = async (
  id: number
): Promise<Product | undefined> => {
  const { data, error } = await supabase
    .from("products")
    .select(`*`)
    .eq("id", id.toString());

  return data[0];
};

/**
 * Slugs of all live products - used for the product detail page paths at build time.
 * Only selects the slug, as loading every product with its tags & variants times out.
 */
export const getAllProductSlugs = async (): Promise<string[]> => {
  const { data, error } = await supabase
    .from("products")
    .select("slug")
    .is("deleted_at", null)
    .is("archived_at", null)
    .not("published_at", "is", null);

  if (error) {
    Sentry.captureException(error);
    throw error;
  }

  return data.map((product) => product.slug);
};

/**
 * Get a single product by slug - used for product detail page SSG
 */
export const getProductBySlug = async (
  slug: string
): Promise<Product | null> => {
  try {
    const { data, error } = await supabase
      .from("products")
      .select(
        `
        id,
        name,
        created_at,
        updated_at,
        published_at,
        description,
        images,
        product_reference,
        slug,
        accordion1_title,
        accordion1,
        accordion2_title,
        accordion2,
        accordion3_title,
        accordion3,
        rrp,
        product_tags (
          tags (
            id,
            name,
            type,
            slug
          )
        ),
        product_variants (
          id,
          size:size_id (
            id,
            name,
            slug,
            order
          ),
          color: color_id (
            id,
            name,
            slug,
            metadata,
            order
          ),
          sku,
          stock,
          deleted_at
        )`
      )
      .eq("slug", slug)
      .is("deleted_at", null)
      .single();

    if (error || !data) {
      return null;
    }

    // Filter out deleted variants
    const filteredVariants = data.product_variants.filter(
      (variant: any) => !variant.deleted_at
    );
    data.product_variants = filteredVariants;

    return data;
  } catch (error: any) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Get recommended products by concept tag - used for "You might also like"
 */
export const getRecommendedProducts = async (
  conceptTagSlug: string,
  excludeProductId: number,
  limit: number = 4
): Promise<Product[]> => {
  try {
    // First get product IDs that have this concept tag
    const { data: tagData, error: tagError } = await supabase
      .from("product_tags")
      .select(
        `
        product_id,
        tags!inner (
          slug
        )
      `
      )
      .eq("tags.slug", conceptTagSlug)
      .neq("product_id", excludeProductId)
      .limit(limit);

    if (tagError || !tagData || tagData.length === 0) {
      return [];
    }

    const productIds = tagData.map((item: any) => item.product_id);

    // Now fetch those products with full details
    const { data, error } = await supabase
      .from("products")
      .select(
        `
        id,
        name,
        images,
        slug,
        rrp,
        product_tags (
          tags (
            id,
            name,
            type,
            slug
          )
        ),
        product_variants (
          id,
          size:size_id (
            id,
            name,
            slug,
            order
          ),
          color: color_id (
            id,
            name,
            slug,
            metadata,
            order
          ),
          stock,
          deleted_at
        )`
      )
      .in("id", productIds)
      .is("deleted_at", null)
      .is("archived_at", null)
      .not("published_at", "is", null)
      .limit(limit);

    if (error) {
      Sentry.captureException(error);
      return [];
    }

    // Filter out deleted variants
    const products = (data || []).map((product) => {
      const filteredVariants = product.product_variants.filter(
        (variant: any) => !variant.deleted_at
      );
      product.product_variants = filteredVariants;
      return product;
    });

    return products;
  } catch (error: any) {
    Sentry.captureException(error);
    return [];
  }
};

/**
 * Get products by variant IDs - used for Statistics to calculate total price
 */
export const getProductsByVariantIds = async (
  variantIds: number[]
): Promise<Product[]> => {
  if (!variantIds || variantIds.length === 0) return [];

  try {
    // Get product IDs from variant IDs
    const { data: variantData, error: variantError } = await supabase
      .from("product_variants")
      .select("product_id")
      .in("id", variantIds);

    if (variantError || !variantData) {
      return [];
    }

    const productIds = [...new Set(variantData.map((v: any) => v.product_id))];

    if (productIds.length === 0) return [];

    // Fetch those products
    const { data, error } = await supabase
      .from("products")
      .select(
        `
        id,
        name,
        slug,
        rrp,
        images,
        product_tags (
          tags (
            id,
            name,
            type,
            slug
          )
        ),
        product_variants (
          id,
          size:size_id (
            id,
            name,
            slug,
            order
          ),
          color: color_id (
            id,
            name,
            slug,
            metadata,
            order
          ),
          stock,
          deleted_at
        )`
      )
      .in("id", productIds);

    if (error) {
      Sentry.captureException(error);
      return [];
    }

    // Filter out deleted variants
    const products = (data || []).map((product) => {
      const filteredVariants = product.product_variants.filter(
        (variant: any) => !variant.deleted_at
      );
      product.product_variants = filteredVariants;
      return product;
    });

    return products;
  } catch (error: any) {
    Sentry.captureException(error);
    return [];
  }
};

/**
 * Get multiple products by their IDs - used for cart validation
 * Only fetches fields needed for stock checking
 */
export const getProductsByIds = async (
  ids: number[]
): Promise<Product[]> => {
  if (!ids || ids.length === 0) return [];

  try {
    const { data, error } = await supabase
      .from("products")
      .select(
        `
        id,
        name,
        slug,
        rrp,
        images,
        product_tags (
          tags (
            id,
            name,
            type,
            slug
          )
        ),
        product_variants (
          id,
          size:size_id (
            id,
            name,
            slug,
            order
          ),
          color: color_id (
            id,
            name,
            slug,
            metadata,
            order
          ),
          stock,
          deleted_at
        )`
      )
      .in("id", ids);

    if (error) {
      Sentry.captureException(error);
      throw error;
    }

    // Filter out deleted variants
    const productsWithFilteredVariants = (data || []).map((product) => {
      const filteredVariants = product.product_variants.filter(
        (variant: any) => !variant.deleted_at
      );
      product.product_variants = filteredVariants;
      return product;
    });

    return productsWithFilteredVariants;
  } catch (error: any) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getAllProducts = async (): Promise<Product[]> => {
  // TODO: make this type the productDB one
  try {
    const { data, error } = await supabase
      .from("products")
      .select(
        `
        id,
        name,
        created_at,
        updated_at,
        published_at,
        description,
        images,
        product_reference,
        slug,
        accordion1_title,
        accordion1,
        accordion2_title,
        accordion2,
        accordion3_title,
        accordion3,
        rrp,
        product_tags (
          tags (
            id,
            name,
            type,
            slug
        )
        ),
        product_variants (
          id,
          size:size_id (
            id,
            name,
            slug,
            order
          ),
          color: color_id (
            id,
            name,
            slug,
            metadata,
            order
          ),
          sku,
          stock,
          deleted_at
        )`
      )
      .is("deleted_at", null)
      .is("archived_at", null)
      .not("published_at", "is", null)
      .order("orderKey", { ascending: true });

    if (error) {
      Sentry.captureException(error);
      throw error;
    }

    const datahiddenDeletedVariants = data.map((product) => {
      const filteredVariants = product.product_variants.filter((variant) => {
        if (!variant.deleted_at) {
          return variant;
        }
      });
      product.product_variants = filteredVariants;
      return product;
    });

    return datahiddenDeletedVariants;
  } catch (error: any) {
    Sentry.captureException(error);
    throw error;
  }
};

/**
 * Get paginated products with filtering and search
 *
 * Uses the `get_filtered_products` RPC function for server-side filtering.
 * See: modules/@supabase/sql/get_filtered_products.sql
 *
 * FILTER LOGIC (handled by RPC):
 * - OR within a tag type: e.g., (size-6m OR size-9m)
 * - AND across tag types: e.g., (any matching size) AND (any matching category)
 * - Availability filter: 'product-available' checks total stock > 0
 * - Previously rented: filters by variant IDs
 *
 * @param filters - Filter options (tags, search, availability)
 * @param page - Page number (1-indexed)
 * @param pageSize - Number of products per page
 */
export const getPaginatedProducts = async (
  filters: ProductFilters = {},
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedProductsResult> => {
  try {
    const offset = (page - 1) * pageSize;

    // Call the RPC function which handles all filtering server-side
    const { data, error } = await supabase.rpc("get_filtered_products", {
      p_tag_slugs: filters.tagSlugs && filters.tagSlugs.length > 0
        ? filters.tagSlugs
        : null,
      p_search_text: filters.searchText || null,
      p_previous_variant_ids: filters.previousProductVariantIds && filters.previousProductVariantIds.length > 0
        ? filters.previousProductVariantIds
        : null,
      p_page_size: pageSize,
      p_page_offset: offset,
    });

    if (error) {
      Sentry.captureException(error);
      throw error;
    }

    // RPC returns pageSize + 1 results to determine if there are more
    const products = data || [];
    const hasMore = products.length > pageSize;
    const paginatedProducts = products.slice(0, pageSize);

    return {
      products: paginatedProducts,
      totalCount: 0, // Not calculated to avoid expensive full table scan
      page,
      pageSize,
      hasMore,
    };
  } catch (error: any) {
    Sentry.captureException(error);
    throw error;
  }
};
